'use client';

import React, { useContext, useEffect, useRef } from 'react';
import type { Ref } from 'react';
import { BaseCollection, CollectionNode, createBranchComponent } from '@react-aria/collections';
import { mergeProps, useHover, useMenuItem, useObjectRef, useSubmenuTrigger } from 'react-aria';
import { useSubmenuTriggerState } from 'react-stately';
import type { SubmenuTriggerProps as AriaSubmenuTriggerProps } from 'react-aria-components';
import {
    MenuContext as AriaMenuContext,
    MenuStateContext,
    OverlayTriggerStateContext,
    PopoverContext,
    Provider,
    RootMenuTriggerStateContext,
} from 'react-aria-components';
import type { Key, Node, RefObject as AriaRefObject } from '@react-types/shared';
import { cx } from '../../../utils/cx';
import type { PopoverProps } from '../popover';
import { Popover } from '../popover';
import { MenuContext as RomaMenuContext } from './menuContext';
import { MenuSubmenuContext } from './menuSubmenuContext';
import type { MenuSubmenuTriggerMode } from './menuSubmenuContext';
import { MenuItemStructuredBase } from './MenuItem';
import { styles } from './menuItemStyles';
import type { MenuItemStructuredProps } from './menuTypes';
import { useMenuSubmenuClickModeSuppression, useMenuSubmenuPointerMode } from './hooks/useMenuSubmenuPointerMode';

const MENU_SUBMENU_DISPLAY_NAME = 'RomaMenuSubmenu';
const MENU_SUBMENU_POPOVER_DISPLAY_NAME = 'RomaMenuSubmenuPopover';

export type MenuSubmenuPopoverProps = PopoverProps;

export type MenuSubmenuChildren = [JSX.Element, JSX.Element];

export type MenuSubmenuProps = Omit<AriaSubmenuTriggerProps, 'children'> & {
    children: MenuSubmenuChildren;
    triggerMode?: MenuSubmenuTriggerMode;
};

export const MENU_SUBMENU_CHILDREN_ERROR =
    'Menu.Submenu requires exactly two children: a Menu.Item trigger and a Menu.SubmenuPopover.';

/**
 * Submenus must have exactly two children: a trigger and a popover.
 */
export const resolveMenuSubmenuChildren = (children: React.ReactNode): MenuSubmenuChildren => {
    const resolved = React.Children.toArray(children);
    const [triggerChild, popoverChild] = resolved;

    if (resolved.length !== 2) throw new Error(MENU_SUBMENU_CHILDREN_ERROR);
    if (!React.isValidElement(triggerChild)) throw new Error(MENU_SUBMENU_CHILDREN_ERROR);
    if (!React.isValidElement(popoverChild)) throw new Error(MENU_SUBMENU_CHILDREN_ERROR);

    return [triggerChild, popoverChild] as MenuSubmenuChildren;
};

/**
 * Represents a submenu trigger in the React Aria collection tree.
 */
class MenuSubmenuNode<T> extends CollectionNode<T> {
    static readonly type = 'submenutrigger';

    /**
     * Keeps submenu filtering based on the trigger row text, matching React Aria Components.
     */
    filter(
        collection: BaseCollection<T>,
        newCollection: BaseCollection<T>,
        filterFn: (textValue: string, node: Node<T>) => boolean,
    ): CollectionNode<T> | null {
        const triggerNode = collection.getItem(this.firstChildKey as Key);
        if (!triggerNode || !filterFn(triggerNode.textValue, this)) return null;

        const clone = this.clone();
        newCollection.addDescendants(clone, collection);
        return clone;
    }
}

/**
 * The collection item passed to createBranchComponent render includes firstChildKey
 * from CollectionNode, which is not part of the public Node<T> interface.
 */
type SubmenuCollectionItem = Node<object> & { firstChildKey?: Key | null };

/**
 * Adapts Roma's custom submenu trigger row into React Aria's collection and submenu state model.
 */
export const MenuSubmenu = createBranchComponent(
    MenuSubmenuNode,
    (props: MenuSubmenuProps, ref: Ref<HTMLDivElement>, item: Node<object>) => {
        const submenuItem = item as SubmenuCollectionItem;
        // Parent menu's tree state, used by useMenuItem for focus and selection management.
        const menuState = useContext(MenuStateContext);
        // Root-level menu trigger state that tracks the full stack of open submenus.
        const rootMenuTriggerState = useContext(RootMenuTriggerStateContext)!;
        const { menuRef: parentMenuRef } = useContext(RomaMenuContext);
        const { getSubmenuTriggerMode, registerSubmenuTriggerMode } = useContext(MenuSubmenuContext);

        // Tracks this submenu's open/close state and its level within the menu tree.
        const submenuTriggerState = useSubmenuTriggerState({ triggerKey: submenuItem.key }, rootMenuTriggerState);
        const submenuRef = useRef<HTMLDivElement>(null);
        const itemRef = useObjectRef(ref);

        const triggerMode = props.triggerMode ?? 'hover';
        const triggerItemKey = submenuItem.firstChildKey ?? submenuItem.key;
        const isClickTriggerMode = triggerMode === 'click';

        // Register this trigger's mode so siblings can look it up for hover/click coordination.
        useEffect(
            () => registerSubmenuTriggerMode(submenuItem.key, triggerMode),
            [submenuItem.key, registerSubmenuTriggerMode, triggerMode],
        );

        // Preserves click-mode pointer behavior while allowing keyboard focus to return on submenu close.
        useMenuSubmenuClickModeSuppression({
            triggerMode,
            parentMenuRef,
            itemRef,
            submenuTriggerState,
        });

        // Provides ARIA props, keyboard/hover/press handlers, and popover config for the submenu.
        const { submenuTriggerProps, submenuProps, popoverProps } = useSubmenuTrigger(
            {
                parentMenuRef: parentMenuRef as AriaRefObject<HTMLElement | null>,
                submenuRef,
                delay: props.delay,
            },
            submenuTriggerState,
            itemRef,
        );

        const triggerProps = {
            ...submenuTriggerProps,
            ...(isClickTriggerMode ? { onHoverChange: undefined } : {}),
        };

        // Wraps press/hover callbacks to implement click-mode vs hover-mode pointer interactions.
        const pointerMode = useMenuSubmenuPointerMode({
            triggerMode,
            triggerKey: submenuItem.key,
            rootMenuTriggerState: rootMenuTriggerState,
            submenuTriggerState,
            getSubmenuTriggerMode,
            onPress: triggerProps.onPress as ((event: unknown) => void) | undefined,
            onHoverChange: triggerProps.onHoverChange as ((isHovered: boolean) => void) | undefined,
        });

        // Integrates the trigger row into the parent menu's focus and keyboard navigation.
        const { menuItemProps, isFocused, isFocusVisible, isDisabled } = useMenuItem(
            {
                key: triggerItemKey,
                id: triggerProps.id as string,
                'aria-controls': triggerProps['aria-controls'] as string | undefined,
                'aria-haspopup': triggerProps['aria-haspopup'] as 'menu' | 'dialog' | undefined,
                'aria-expanded': triggerProps['aria-expanded'] as boolean | 'true' | 'false' | undefined,
                onPressStart: triggerProps.onPressStart,
                onPress: pointerMode.onPress,
                onHoverStart: pointerMode.onHoverStart,
                onHoverChange: pointerMode.onHoverChange,
                onKeyDown: triggerProps.onKeyDown,
            },
            menuState!,
            itemRef,
        );

        // Tracks pointer hover state for the trigger row's visual highlight.
        const { hoverProps, isHovered } = useHover({});
        const [triggerChild, popoverChild] = resolveMenuSubmenuChildren(props.children);
        const triggerChildProps = triggerChild.props as MenuItemStructuredProps;

        return (
            <Provider
                values={[
                    [
                        AriaMenuContext,
                        {
                            ref: submenuRef,
                            ...submenuProps,
                        },
                    ],
                    [OverlayTriggerStateContext, submenuTriggerState],
                    [
                        PopoverContext,
                        {
                            trigger: 'SubmenuTrigger',
                            triggerRef: itemRef,
                            placement: 'end top',
                            'aria-labelledby': submenuProps['aria-labelledby'],
                            ...popoverProps,
                        },
                    ],
                ]}
            >
                <div
                    {...mergeProps(menuItemProps, hoverProps)}
                    ref={itemRef}
                    data-click-only-trigger={isClickTriggerMode || undefined}
                    className={cx(MENU_SUBMENU_DISPLAY_NAME, styles.common.menuItem)}
                >
                    <MenuItemStructuredBase
                        leading={triggerChildProps.leading}
                        label={triggerChildProps.label}
                        description={triggerChildProps.description}
                        trailing={triggerChildProps.trailing}
                        destructive={triggerChildProps.destructive}
                        hasSubmenu
                        isOpen={submenuTriggerState.isOpen}
                        isFocused={isFocused}
                        isFocusVisible={isFocusVisible}
                        isHovered={isHovered}
                        isDisabled={isDisabled}
                    />
                </div>
                {popoverChild}
            </Provider>
        );
    },
    (props: MenuSubmenuProps) => resolveMenuSubmenuChildren(props.children)[0],
);

/**
 * Thin wrapper over Roma `Popover` for menu submenus.
 * Defaults the submenu offset while still allowing callers to override it.
 */
export const MenuSubmenuPopover = ({ offset, crossOffset, ...props }: MenuSubmenuPopoverProps): JSX.Element => (
    <Popover {...props} offset={offset ?? 2} crossOffset={crossOffset ?? -4} />
);

(MenuSubmenu as { displayName?: string }).displayName = MENU_SUBMENU_DISPLAY_NAME;
MenuSubmenuPopover.displayName = MENU_SUBMENU_POPOVER_DISPLAY_NAME;
